// Pure, dependency-free helpers: only *types* are imported (erased at build), so this module has no
// runtime dependency on the store/providers/Vue and stays trivially unit-testable.
import type { ChartDataType } from "@/features/Charts";
import type {
  DashboardProjectBreakdownType,
  DashboardProjectBreakdownClassificationType,
  DashboardProjectBreakdownTypeType,
  DashboardProjectBreakdownMaterialType,
  DashboardProjectBreakdownConstructionType,
} from "@/features/Dashboard";

export type HotspotGroupBy = "material" | "type";
export type StageBucketKey = "A1A3" | "A4" | "A5" | "B" | "C" | "D";

export type StageBucket = {
  key: StageBucketKey;
  label: string;
  // ECharts requires literal hex; values mirror the design tokens noted alongside each.
  color: string;
};

// Order matches the ticket mockup legend: A1–A3 · A4 · A5 · B · C · D.
export const STAGE_BUCKETS: StageBucket[] = [
  { key: "A1A3", label: "A1–A3", color: "#2036ff" }, // app-blue-600
  { key: "A4", label: "A4", color: "#6aae34" }, // app-green-500
  { key: "A5", label: "A5", color: "#ef9f11" }, // app-yellow-500
  { key: "B", label: "B", color: "#a072da" }, // app-purple-500
  { key: "C", label: "C", color: "#ee4745" }, // app-red-500
  { key: "D", label: "D", color: "#131953" }, // app-blue-950
];

// Colour for the single "total" bar when the stage breakdown is turned off.
const TOTAL_SERIES_COLOR = "#2036ff"; // app-blue-600

export type StageModuleKey =
  | "A1A3"
  | "A4"
  | "A5"
  | "B1"
  | "B2"
  | "B3"
  | "B4"
  | "B5"
  | "B6"
  | "B7"
  | "C1"
  | "C2"
  | "C3"
  | "C4"
  | "D";

export type StageModule = { key: StageModuleKey; label: string };

// Every raw stage module (A1A3, A4, A5, B1..B7, C1..C4, D), in legend order. Unlike STAGE_BUCKETS this
// does NOT collapse B1-B7/C1-C4 — use it when a caller needs per-module detail rather than the coarse stage.
export const STAGE_MODULES: StageModule[] = [
  { key: "A1A3", label: "A1–A3" },
  { key: "A4", label: "A4" },
  { key: "A5", label: "A5" },
  { key: "B1", label: "B1" },
  { key: "B2", label: "B2" },
  { key: "B3", label: "B3" },
  { key: "B4", label: "B4" },
  { key: "B5", label: "B5" },
  { key: "B6", label: "B6" },
  { key: "B7", label: "B7" },
  { key: "C1", label: "C1" },
  { key: "C2", label: "C2" },
  { key: "C3", label: "C3" },
  { key: "C4", label: "C4" },
  { key: "D", label: "D" },
];

const emptyBuckets = (): Record<StageBucketKey, number> => ({ A1A3: 0, A4: 0, A5: 0, B: 0, C: 0, D: 0 });

// Map a raw stage key (A1A3, A4, A5, B1..B7, C1..C4, D) to its collapsed display bucket.
export function bucketForStage(stageKey: string): StageBucketKey | null {
  if (stageKey === "A1A3") return "A1A3";
  if (stageKey === "A4") return "A4";
  if (stageKey === "A5") return "A5";
  if (stageKey.startsWith("B")) return "B";
  if (stageKey.startsWith("C")) return "C";
  if (stageKey.startsWith("D")) return "D";
  return null;
}

// Admits a bucket when ANY of its raw stages is selected (picking B4 alone still shows the B bucket).
// Empty selection means "no stage filter", matching the store's semantics when nothing is selected.
export function bucketsForSelectedStages(selectedStages: string[] | undefined): StageBucketKey[] {
  if (!selectedStages?.length) return STAGE_BUCKETS.map((bucket) => bucket.key);

  const admitted = new Set<StageBucketKey>();
  for (const stageKey of selectedStages) {
    const bucket = bucketForStage(stageKey);
    if (bucket) admitted.add(bucket);
  }

  return STAGE_BUCKETS.filter((bucket) => admitted.has(bucket.key)).map((bucket) => bucket.key);
}

// Client-side twin of the `stages` argument the dashboard endpoint takes. Matches RAW stage keys
// (not display buckets), so selecting B4 alone scopes to B4 rather than the whole B bucket.
export function scopeRawStagesToSelection(
  rawStages: Record<string, number> | undefined,
  selectedStages: string[] | undefined
): Record<string, number> {
  if (!rawStages) return {};
  if (!selectedStages?.length) return rawStages;

  const admitted = new Set(selectedStages);
  const scoped: Record<string, number> = {};
  for (const [stageKey, value] of Object.entries(rawStages)) {
    if (admitted.has(stageKey)) scoped[stageKey] = value;
  }
  return scoped;
}

// Sum a material's per-stage emission map into the six display buckets.
export function bucketStages(emissionByStage: Record<string, number> | undefined): Record<StageBucketKey, number> {
  const buckets = emptyBuckets();
  if (!emissionByStage) return buckets;
  for (const [stageKey, value] of Object.entries(emissionByStage)) {
    const bucket = bucketForStage(stageKey);
    if (bucket) buckets[bucket] += value ?? 0;
  }
  return buckets;
}

// The backend leaves B6 and D out of a material's own total (DashboardConstants
// .ExcludedStagesForMaterialTotal); `emissionByStage` carries them in full, so a frontend total must subtract them.
export const MATERIAL_TOTAL_EXCLUDED_STAGES = ["B6", "D"];

const MATERIAL_TOTAL_EXCLUDED_STAGE_SET = new Set(MATERIAL_TOTAL_EXCLUDED_STAGES);

// Takes the *raw* stage map, not the six display buckets: `bucketForStage` collapses B1-B7 into one
// `B` bucket, so B6 is no longer separable once bucketed. Negatives are preserved (a C credit counts, a D credit doesn't).
export function sumMaterialTotal(rawStages: Record<string, number> | undefined): number {
  if (!rawStages) return 0;
  return Object.entries(rawStages).reduce(
    (sum, [stageKey, value]) => (MATERIAL_TOTAL_EXCLUDED_STAGE_SET.has(stageKey) ? sum : sum + (value ?? 0)),
    0
  );
}

export type HotspotRow = {
  id: string;
  label: string;
  model: string;
  total: number;
  stages: Record<StageBucketKey, number>;
  // The per-stage map exactly as the backend sends it, kept alongside the collapsed buckets so a
  // caller needing a single raw stage back (e.g. `sumMaterialTotal` needing B6 out of `B`) still has it.
  rawStages: Record<string, number>;
};

type MaterialNode = DashboardProjectBreakdownMaterialType[string];
type ConstructionNode = DashboardProjectBreakdownConstructionType[string];
type TypeNode = DashboardProjectBreakdownTypeType[string];
type ClassificationMap = DashboardProjectBreakdownClassificationType;

// Collect every material leaf under a type (direct materials + materials nested in constructions).
function materialsInType(type: TypeNode): MaterialNode[] {
  const materials: MaterialNode[] = [];
  if (type.materials) materials.push(...Object.values(type.materials));
  if (type.constructions) {
    for (const construction of Object.values(type.constructions)) {
      if (construction.materials) materials.push(...Object.values(construction.materials));
    }
  }
  return materials;
}

// Walk the (recursive) classification tree and hand every type node to the visitor. `skipSite` leaves
// out the Talo 2000 site branch (`isSite`), which counts only in the Site figure, never in a chart.
function forEachType(classifications: ClassificationMap | undefined, visit: (type: TypeNode) => void, skipSite = false): void {
  if (!classifications) return;
  for (const classification of Object.values(classifications)) {
    if (skipSite && classification.isSite) continue;
    if (classification.types) {
      for (const type of Object.values(classification.types)) visit(type);
    }
    forEachType(classification.classifications, visit, skipSite);
  }
}

// Shared row accumulator: folds material leaves into rows keyed however the caller chooses, then
// hands them back sorted by total emission descending.
function createRowAccumulator() {
  const rows = new Map<string, HotspotRow>();

  const add = (key: string, label: string, model: string, material: MaterialNode) => {
    const row = rows.get(key) ?? { id: key, label, model, total: 0, stages: emptyBuckets(), rawStages: {} };
    // Accumulate the raw stage keys before bucketing — bucketing is lossy (every B* becomes `B`),
    // and `sumMaterialTotal` has to be able to single out B6.
    for (const [stageKey, value] of Object.entries(material.emissionByStage ?? {})) {
      row.rawStages[stageKey] = (row.rawStages[stageKey] ?? 0) + (value ?? 0);
    }
    const staged = bucketStages(material.emissionByStage);
    for (const bucket of STAGE_BUCKETS) row.stages[bucket.key] += staged[bucket.key];
    // Derived from the bucketed stages, not the backend's `material.emission`, so the stage-breakdown
    // view and the single-total-bar view always agree on the number.
    row.total = STAGE_BUCKETS.reduce((sum, bucket) => sum + row.stages[bucket.key], 0);
    rows.set(key, row);
  };

  const sortedRows = (): HotspotRow[] => Array.from(rows.values()).sort((a, b) => b.total - a.total);

  return { add, sortedRows };
}

// Flattens the project-breakdown tree into rows grouped by material or type. Pure — the material
// translator is injected so this can be unit-tested without Vue/Pinia/providers.
export function buildHotspotRows(
  projectBreakdown: DashboardProjectBreakdownType | undefined,
  groupBy: HotspotGroupBy,
  translateMaterial: (names: MaterialNode["names"] | undefined, fallback: string) => string
): HotspotRow[] {
  if (!projectBreakdown?.classifications) return [];

  const accumulator = createRowAccumulator();

  forEachType(
    projectBreakdown.classifications,
    (type) => {
      const materials = materialsInType(type);
      if (groupBy === "type") {
        for (const material of materials) accumulator.add(type.name, type.name, "", material);
      } else {
        for (const material of materials) {
          const key = material.id || material.name;
          accumulator.add(key, translateMaterial(material.names, material.name), material.model ?? "", material);
        }
      }
    },
    true
  );

  return accumulator.sortedRows();
}

// Rows keyed by *project material id*, the id the mapping surfaces know a material by, unlike
// `buildHotspotRows`: a construction mapping's layers carry synthetic ids, so each construction collapses into one row here.
export function buildProjectMaterialRows(
  projectBreakdown: DashboardProjectBreakdownType | undefined,
  translateMaterial: (names: MaterialNode["names"] | undefined, fallback: string) => string
): HotspotRow[] {
  if (!projectBreakdown?.classifications) return [];

  const accumulator = createRowAccumulator();

  const addConstruction = (construction: ConstructionNode) => {
    const key = construction.id || construction.name;
    // Constructions carry no `names` map of their own, so the assembly name stands as the label.
    for (const layer of Object.values(construction.materials ?? {})) {
      accumulator.add(key, construction.name, construction.model ?? "", layer);
    }
  };

  forEachType(projectBreakdown.classifications, (type) => {
    for (const material of Object.values(type.materials ?? {})) {
      const key = material.id || material.name;
      accumulator.add(key, translateMaterial(material.names, material.name), material.model ?? "", material);
    }
    for (const construction of Object.values(type.constructions ?? {})) addConstruction(construction);
  });

  return accumulator.sortedRows();
}

// `buildProjectMaterialRows` keys a row by id, falling back to name for nodes without one — a caller
// holding a project material has two candidate keys and must try them in that order.
export function findProjectMaterialRow(rows: HotspotRow[], ...keys: Array<string | undefined | null>): HotspotRow | null {
  for (const key of keys) {
    if (!key) continue;
    const match = rows.find((row) => row.id === key);
    if (match) return match;
  }
  return null;
}

// Converts rows into the ChartDataType shape HorizontalBarChart expects. Every series must carry an
// entry for every row in the same order, or the shared y-axis categories drift out of alignment across the stack.
export function rowsToChartData(
  rows: HotspotRow[],
  options: { showStageBreakdown: boolean; activeStages: StageBucketKey[]; mode?: "absolute" | "relative" }
): ChartDataType {
  const { showStageBreakdown, activeStages, mode = "absolute" } = options;

  if (!showStageBreakdown) {
    return {
      total: {
        name: "Total",
        color: TOTAL_SERIES_COLOR,
        entries: rows.map((row) => ({ id: row.id, label: row.label, value: row.total })),
      },
    };
  }

  // "Relative" mode normalizes each bar to the sum of ABSOLUTE stage values, not the signed sum — a
  // signed denominator near zero (credits offsetting emissions) threw segments past 100% and flipped their signs.
  const relative = mode === "relative";
  const activeMagnitudes = rows.map((row) => activeStages.reduce((sum, key) => sum + Math.abs(row.stages[key]), 0));

  const data: ChartDataType = {};
  for (const bucket of STAGE_BUCKETS) {
    if (!activeStages.includes(bucket.key)) continue;
    data[bucket.key] = {
      name: bucket.label,
      color: bucket.color,
      stack: "total",
      entries: rows.map((row, index) => {
        const raw = row.stages[bucket.key];
        const magnitude = activeMagnitudes[index];
        return { id: row.id, label: row.label, value: relative ? (magnitude ? (raw / magnitude) * 100 : 0) : raw };
      }),
    };
  }
  return data;
}
